// src/components/marketing/ContentGeneratorPage.tsx
// Track E4 — AI content generator (A10).
//
// Prompt form at top. Latest generation rendered inline below the form.
// History drawer underneath lists past runs with filters; each row
// expands to show the full output + variants + token cost.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw,
  Sparkles, Wand2,
} from 'lucide-react';

import {
  ApiError, generateContent, listContent,
  type ContentGeneration, type ContentKind, type GenerateContentIn,
  type GenerationStatus, type Paginated, type TargetPlatform,
} from '@/lib/api_ai_marketing';

type Notifier = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string; duration?: number }) => void;

interface Props { addNotification?: Notifier; }

const CONTENT_KINDS: Array<{ value: ContentKind; label: string }> = [
  { value: 'caption',      label: 'Caption (redes)' },
  { value: 'headline',     label: 'Titular' },
  { value: 'ad_copy',      label: 'Ad copy' },
  { value: 'ad_variants',  label: 'Variantes A/B' },
  { value: 'hashtag_set',  label: 'Set de hashtags' },
  { value: 'cta',          label: 'Call-to-action' },
  { value: 'long_form',    label: 'Long-form' },
];

const PLATFORMS: Array<{ value: TargetPlatform; label: string }> = [
  { value: 'generic',   label: 'Genérico' },
  { value: 'facebook',  label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'google',    label: 'Google Ads' },
  { value: 'tiktok',    label: 'TikTok' },
  { value: 'email',     label: 'Email' },
  { value: 'whatsapp',  label: 'WhatsApp' },
];

export function ContentGeneratorPage({ addNotification }: Props) {
  // ── Form state ─────────────────────────────────────────────
  const [kind, setKind] = useState<ContentKind>('caption');
  const [platform, setPlatform] = useState<TargetPlatform>('instagram');
  const [prompt, setPrompt] = useState('');
  const [variants, setVariants] = useState(1);
  const [temperature, setTemperature] = useState(0.7);

  // ── Run state ──────────────────────────────────────────────
  const [latest, setLatest] = useState<ContentGeneration | null>(null);
  const [generating, setGenerating] = useState(false);

  // ── History state ──────────────────────────────────────────
  const [history, setHistory] = useState<Paginated<ContentGeneration> | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState<{ kind?: ContentKind; status?: GenerationStatus }>({});
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const rows = await listContent({
        content_type: historyFilter.kind,
        status: historyFilter.status,
        limit: 25,
      });
      setHistory(rows);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.message : (e?.message || 'Error cargando historial');
      addNotification?.({ type: 'error', title: 'Error', message: msg });
    } finally {
      setHistoryLoading(false);
    }
  }, [historyFilter, addNotification]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() || generating) return;
    const payload: GenerateContentIn = {
      content_type: kind,
      target_platform: platform,
      prompt: prompt.trim(),
      requested_variants: Math.max(1, Math.min(10, variants)),
      temperature,
    };
    setGenerating(true);
    try {
      const row = await generateContent(payload);
      setLatest(row);
      // Prepend to history so the user sees it immediately without a roundtrip.
      setHistory((prev) => prev ? {
        items: [row, ...prev.items].slice(0, 25),
        count: prev.count + 1,
      } : prev);
      if (row.status === 'failed') {
        addNotification?.({
          type: 'error', title: 'Generación falló',
          message: row.error_message || 'El LLM regresó un error.',
        });
      } else {
        addNotification?.({
          type: 'success', title: 'Generado',
          message: `${row.provider || 'LLM'} · ${row.total_tokens ?? 0} tokens`,
          duration: 3000,
        });
      }
    } catch (err: any) {
      const msg = err instanceof ApiError ? err.message : (err?.message || 'Error');
      addNotification?.({ type: 'error', title: 'No se pudo generar', message: msg });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* ── Form ──────────────────────────────────────────── */}
      <form onSubmit={submit} className="o-card p-5 space-y-4">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Sparkles size={18} className="text-[var(--acc1,#00e5ff)]" />
          Generador de contenido
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Tipo">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as ContentKind)}
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
            >
              {CONTENT_KINDS.map((k) => (
                <option key={k.value} value={k.value} className="bg-[var(--panel,#1e1f2b)]">
                  {k.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Plataforma destino">
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as TargetPlatform)}
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none"
            >
              {PLATFORMS.map((p) => (
                <option key={p.value} value={p.value} className="bg-[var(--panel,#1e1f2b)]">
                  {p.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Prompt" hint="Describe qué quieres generar. En español o el idioma que prefieras.">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="Caption para un post de Instagram sobre un viaje a Chiapas en temporada de lluvias…"
            className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 outline-none focus:border-[var(--acc1,#00e5ff)]/50 font-normal"
            required
          />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label={`Variantes (${variants})`} hint="Cada variante es una llamada independiente al LLM.">
            <input
              type="range"
              min={1}
              max={5}
              value={variants}
              onChange={(e) => setVariants(parseInt(e.target.value, 10))}
              className="w-full accent-[var(--acc1,#00e5ff)]"
            />
          </Field>
          <Field label={`Creatividad (${temperature.toFixed(1)})`} hint="0 = predecible · 1 = creativo · >1 = arriesgado.">
            <input
              type="range"
              min={0}
              max={1}
              step={0.1}
              value={temperature}
              onChange={(e) => setTemperature(parseFloat(e.target.value))}
              className="w-full accent-[var(--acc1,#00e5ff)]"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="submit"
            disabled={!prompt.trim() || generating}
            className="px-4 py-2 rounded-md bg-[var(--acc1,#00e5ff)]/20 text-[var(--acc1,#00e5ff)] hover:bg-[var(--acc1,#00e5ff)]/30 font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            {generating ? 'Generando…' : 'Generar'}
          </button>
        </div>
      </form>

      {/* ── Latest result ─────────────────────────────────── */}
      {latest && <GenerationCard generation={latest} expanded showLabel="Última generación" />}

      {/* ── History ───────────────────────────────────────── */}
      <div className="o-card p-5">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-base font-semibold">Historial</h2>
          <div className="flex-1" />
          <select
            value={historyFilter.kind ?? ''}
            onChange={(e) => setHistoryFilter((f) => ({ ...f, kind: (e.target.value || undefined) as ContentKind | undefined }))}
            className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm"
          >
            <option value="" className="bg-[var(--panel,#1e1f2b)]">Todos los tipos</option>
            {CONTENT_KINDS.map((k) => (
              <option key={k.value} value={k.value} className="bg-[var(--panel,#1e1f2b)]">
                {k.label}
              </option>
            ))}
          </select>
          <select
            value={historyFilter.status ?? ''}
            onChange={(e) => setHistoryFilter((f) => ({ ...f, status: (e.target.value || undefined) as GenerationStatus | undefined }))}
            className="px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm"
          >
            <option value="" className="bg-[var(--panel,#1e1f2b)]">Todos los estados</option>
            <option value="completed" className="bg-[var(--panel,#1e1f2b)]">Completados</option>
            <option value="failed" className="bg-[var(--panel,#1e1f2b)]">Fallidos</option>
            <option value="pending" className="bg-[var(--panel,#1e1f2b)]">Pendientes</option>
          </select>
          <button
            type="button"
            onClick={loadHistory}
            disabled={historyLoading}
            className="o-btn-ghost p-1.5"
            title="Refrescar"
          >
            <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        {historyLoading && !history ? (
          <div className="py-10 text-center opacity-60">
            <Loader2 className="inline animate-spin" size={18} />
          </div>
        ) : !history || history.items.length === 0 ? (
          <div className="py-10 text-center text-sm text-white/50">
            Sin generaciones todavía. Prueba el formulario de arriba.
          </div>
        ) : (
          <div className="space-y-2">
            {history.items.map((g) => (
              <GenerationCard
                key={g.id}
                generation={g}
                expanded={openIds.has(g.id)}
                onToggle={() => setOpenIds((s) => {
                  const next = new Set(s);
                  if (next.has(g.id)) next.delete(g.id); else next.add(g.id);
                  return next;
                })}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Generation card
// ═══════════════════════════════════════════════════════════════

function GenerationCard({
  generation,
  expanded,
  onToggle,
  showLabel,
}: {
  generation: ContentGeneration;
  expanded: boolean;
  onToggle?: () => void;
  showLabel?: string;
}) {
  const { status, content_type, target_platform, prompt, output_text, output_variants, total_tokens, provider, model, error_message, created_at } = generation;

  const head = (
    <div className="flex items-center gap-2 text-sm">
      {onToggle && (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
      <StatusBadge status={status} />
      <span className="text-white/70">{CONTENT_KINDS.find((c) => c.value === content_type)?.label ?? content_type}</span>
      <span className="text-white/40">·</span>
      <span className="text-white/50">{target_platform}</span>
      {total_tokens !== null && (
        <>
          <span className="text-white/40">·</span>
          <span className="text-white/50">{total_tokens} tokens</span>
        </>
      )}
      <div className="flex-1" />
      <span className="text-xs text-white/40">{formatDate(created_at)}</span>
    </div>
  );

  return (
    <div className={`rounded-lg border ${expanded ? 'border-[var(--acc1,#00e5ff)]/30 bg-[var(--acc1,#00e5ff)]/5' : 'border-white/10 bg-white/5'}`}>
      {showLabel && (
        <div className="px-4 pt-3 text-xs uppercase tracking-wider text-white/50">
          {showLabel}
        </div>
      )}
      {onToggle ? (
        <button type="button" onClick={onToggle} className="w-full text-left px-4 py-3">
          {head}
        </button>
      ) : (
        <div className="px-4 py-3">{head}</div>
      )}

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          {status === 'failed' && error_message && (
            <div className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-sm text-red-200 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="break-words">{error_message}</div>
            </div>
          )}

          <div className="text-xs uppercase tracking-wider text-white/40">Prompt</div>
          <div className="text-sm bg-white/5 p-3 rounded-md whitespace-pre-wrap">{prompt}</div>

          {output_text && (
            <>
              <div className="text-xs uppercase tracking-wider text-white/40 flex items-center gap-2">
                <span>Output</span>
                {provider && <span className="text-white/30">{provider} · {model}</span>}
              </div>
              <OutputBlock text={output_text} />
            </>
          )}

          {output_variants.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-wider text-white/40">
                Variantes ({output_variants.length})
              </div>
              {output_variants.map((v, i) => (
                <OutputBlock key={i} text={v} label={`Variante ${i + 2}`} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function OutputBlock({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="rounded-md bg-white/5 border border-white/10">
      <div className="px-3 py-2 border-b border-white/10 flex items-center gap-2 text-xs text-white/50">
        <span className="uppercase tracking-wider">{label ?? 'Principal'}</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={copy}
          className="flex items-center gap-1 hover:text-white"
          title="Copiar"
        >
          {copied ? <span className="text-emerald-300">✓ Copiado</span> : <><Copy size={12} /> Copiar</>}
        </button>
      </div>
      <pre className="px-3 py-2 text-sm whitespace-pre-wrap font-sans">{text}</pre>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Shared
// ═══════════════════════════════════════════════════════════════

function Field({
  label, hint, children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-white/50">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-xs text-white/40 mt-1 block">{hint}</span>}
    </label>
  );
}

const STATUS_STYLES: Record<GenerationStatus, { bg: string; label: string }> = {
  pending:   { bg: 'bg-amber-500/15 text-amber-300',     label: 'Pendiente' },
  completed: { bg: 'bg-emerald-500/15 text-emerald-300', label: 'Completado' },
  failed:    { bg: 'bg-red-500/15 text-red-300',         label: 'Falló' },
};

function StatusBadge({ status }: { status: GenerationStatus }) {
  const s = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium ${s.bg}`}>
      {s.label}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default ContentGeneratorPage;
